import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = '';
const token = () => localStorage.getItem('token');
const headers = () => ({ 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token() });

async function apiFetch(path, opts = {}) {
  const res = await fetch(API + path, { headers: headers(), ...opts });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw e; }
  return res.json();
}

const BADGE_OPTIONS = [
  { value: '', label: 'Nenhum', emoji: '' },
  { value: 'hot', label: 'HOT', emoji: '\u{1F525}' },
  { value: 'novo', label: 'Novo', emoji: '\u2728' },
  { value: 'ultimas', label: '\u00DAltimas', emoji: '\u26A1' },
  { value: 'esgotado', label: 'Esgotado', emoji: '\u{1F6AB}' },
];

// Fundo local — servido pelo Nginx proxy /uploads/ -> API container
const BG_URL = '/uploads/products/fundo-brahim.png';

export default function NovoProduto({ onBack, editId }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: '', description: '', category_id: '', subcategory_ids: [],
    price: '', compare_price: '', price_label: '\u00e0 vista',
    badge: '', stock: 10, language: '', featured: false, active: true,
    sort_order: 0, custo_unit: '', fornecedor: '', tipo_item: 'lacrado',
    listar_no_site: true, condicao: 'novo', set_colecao: '', estoque_min: 0,
  });
  const [variants, setVariants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Image composer
  const [productImgSrc, setProductImgSrc] = useState(null);
  const [composedUrl, setComposedUrl] = useState(null);
  const [imgScale, setImgScale] = useState(70);
  const [imgX, setImgX] = useState(50);
  const [imgY, setImgY] = useState(50);
  const canvasRef = useRef(null);
  const bgImgRef = useRef(null);
  const prodImgRef = useRef(null);
  const [bgLoaded, setBgLoaded] = useState(false);
  const [prodLoaded, setProdLoaded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Load categories
  useEffect(() => {
    apiFetch('/api/admin/store/categories').then(data => setCategories(data)).catch(() => {});
  }, []);

  // Load product if editing
  useEffect(() => {
    if (editId) {
      apiFetch('/api/admin/store/products/' + editId).then(p => {
        setForm({
          name: p.name || '', description: p.description || '',
          category_id: p.category_id || '', subcategory_ids: p.subcategory_ids || (p.subcategory_id ? [p.subcategory_id] : []),
          price: p.price || '', compare_price: p.compare_price || '',
          price_label: p.price_label || '\u00e0 vista', badge: p.badge || '',
          stock: p.stock || 0, language: p.language || '',
          featured: p.featured || false, active: p.active !== false, sort_order: p.sort_order || 0,
          custo_unit: p.custo_unit || '', fornecedor: p.fornecedor || '',
          tipo_item: p.tipo_item || 'lacrado', listar_no_site: p.listar_no_site !== false,
          condicao: p.condicao || 'novo', set_colecao: p.set_colecao || '',
          estoque_min: p.estoque_min || 0,
        });
        if (p.variants && p.variants.length) setVariants(p.variants);
        if (p.images && p.images[0] && p.images[0].url) {
          setComposedUrl(p.images[0].url);
        }
      }).catch(() => {});
    }
  }, [editId]);

  // Load background image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { bgImgRef.current = img; setBgLoaded(true); };
    img.onerror = () => {
      // Fallback: tenta via API direta
      const img2 = new Image();
      img2.crossOrigin = 'anonymous';
      img2.onload = () => { bgImgRef.current = img2; setBgLoaded(true); };
      img2.src = 'http://' + window.location.hostname + ':4000/uploads/products/fundo-brahim.png';
    };
    img.src = BG_URL;
  }, []);

  // Handle product image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setProductImgSrc(ev.target.result);
      const img = new Image();
      img.onload = () => { prodImgRef.current = img; setProdLoaded(true); };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    setComposedUrl(null);
  };

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bgImgRef.current) return;
    const ctx = canvas.getContext('2d');
    const W = 1080, H = 1080;
    canvas.width = W;
    canvas.height = H;
    ctx.drawImage(bgImgRef.current, 0, 0, W, H);
    if (prodImgRef.current && prodLoaded) {
      const pImg = prodImgRef.current;
      const maxDim = (imgScale / 100) * W;
      const ratio = pImg.width / pImg.height;
      let dw, dh;
      if (ratio > 1) { dw = maxDim; dh = maxDim / ratio; }
      else { dh = maxDim; dw = maxDim * ratio; }
      const dx = (imgX / 100) * W - dw / 2;
      const dy = (imgY / 100) * H - dh / 2;
      ctx.drawImage(pImg, dx, dy, dw, dh);
    }
  }, [bgLoaded, prodLoaded, imgScale, imgX, imgY]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  // Mouse drag on canvas
  const handleMouseDown = (e) => {
    if (!prodLoaded) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, startX: imgX, startY: imgY };
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = 100 / rect.width;
    const scaleY = 100 / rect.height;
    const dx = (e.clientX - dragStart.current.x) * scaleX;
    const dy = (e.clientY - dragStart.current.y) * scaleY;
    setImgX(Math.max(0, Math.min(100, dragStart.current.startX + dx)));
    setImgY(Math.max(0, Math.min(100, dragStart.current.startY + dy)));
  };
  const handleMouseUp = () => setDragging(false);

  // Export composed image
  const exportComposed = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const dataUrl = canvas.toDataURL('image/png');
    try {
      const res = await fetch('/api/admin/store/upload-composed', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ image: dataUrl })
      });
      const data = await res.json();
      setComposedUrl(data.url);
      return data.url;
    } catch (err) {
      setError('Erro ao salvar imagem composta');
      return null;
    }
  };

  const currentCat = categories.find(c => c.id === parseInt(form.category_id));
  const subcats = currentCat ? (currentCat.subcategories || []) : [];

  const toggleSub = (id) => {
    setForm(prev => ({
      ...prev,
      subcategory_ids: prev.subcategory_ids.includes(id)
        ? prev.subcategory_ids.filter(x => x !== id)
        : [...prev.subcategory_ids, id]
    }));
  };

  // Save
  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!form.name.trim()) return setError('Nome \u00e9 obrigat\u00f3rio');
    if (!form.price) return setError('Pre\u00e7o \u00e9 obrigat\u00f3rio');
    if (!form.category_id) return setError('Selecione uma categoria');

    setSaving(true);
    try {
      let imageUrl = composedUrl;
      if (prodLoaded && !composedUrl) {
        imageUrl = await exportComposed();
        if (!imageUrl) { setSaving(false); return; }
      }

      const data = {
        name: form.name.trim(),
        description: form.description.trim(),
        category_id: parseInt(form.category_id),
        subcategory_id: form.subcategory_ids[0] || null,
        subcategory_ids: form.subcategory_ids,
        price: parseFloat(form.price),
        compare_price: form.compare_price ? parseFloat(form.compare_price) : null,
        price_label: form.price_label || '\u00e0 vista',
        badge: form.badge || null,
        stock: parseInt(form.stock) || 0,
        language: form.language || null,
        featured: form.featured,
        active: form.active,
        sort_order: parseInt(form.sort_order) || 0,
        custo_unit: form.custo_unit ? parseFloat(form.custo_unit) : null,
        fornecedor: form.fornecedor || null,
        tipo_item: form.tipo_item || null,
        listar_no_site: form.listar_no_site,
        condicao: form.condicao || null,
        set_colecao: form.set_colecao || null,
        estoque_min: parseInt(form.estoque_min) || 0,
        images: imageUrl ? [{ url: imageUrl, alt: form.name }] : [],
        variants: variants.filter(v => v.name.trim()),
      };

      if (editId) {
        await fetch('/api/admin/store/products/' + editId, {
          method: 'PUT', headers: headers(), body: JSON.stringify(data)
        });
        setSuccess('Produto atualizado!');
      } else {
        await fetch('/api/admin/store/products', {
          method: 'POST', headers: headers(), body: JSON.stringify(data)
        });
        setSuccess('Produto criado!');
      }

      setTimeout(() => { if (onBack) onBack(); }, 1500);
    } catch (err) {
      setError(err.error || 'Erro ao salvar produto');
    }
    setSaving(false);
  };

  // Variant management
  const addVariant = () => setVariants(prev => [...prev, { name: '', color: '#8b5cf6', image_url: '', price_override: '', stock: 0 }]);
  const removeVariant = (i) => setVariants(prev => prev.filter((_, idx) => idx !== i));
  const updateVariant = (i, k, v) => {
    setVariants(prev => { const arr = [...prev]; arr[i] = { ...arr[i], [k]: v }; return arr; });
  };

  // Calcula margem
  const custoNum = parseFloat(form.custo_unit) || 0;
  const precoNum = parseFloat(form.price) || 0;
  const margem = custoNum > 0 ? ((precoNum - custoNum) / custoNum * 100) : null;

  const S = styles;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>{'\u2190'} Voltar</button>
        <h1 style={S.title}>{editId ? 'Editar Produto' : 'Novo Produto'}</h1>
      </div>

      {error && <div style={S.errorMsg}>{error}</div>}
      {success && <div style={S.successMsg}>{success}</div>}

      <div style={S.layout}>
        {/* LEFT — Image composer */}
        <div style={S.leftCol}>
          {/* Toggle listar no site — destaque no topo */}
          <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10,
            background: form.listar_no_site ? 'rgba(139,92,246,0.12)' : 'var(--surface)',
            border: '1px solid ' + (form.listar_no_site ? 'rgba(139,92,246,0.4)' : 'var(--border)'),
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => set('listar_no_site', !form.listar_no_site)}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: form.listar_no_site ? '#a78bfa' : 'var(--text-mid)' }}>
                {form.listar_no_site ? '\u{1F310} Vis\u00edvel na loja online' : '\u{1F512} Somente estoque interno'}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-lo)', marginTop: 2 }}>
                {form.listar_no_site ? 'Clientes podem ver e comprar no site' : 'N\u00e3o aparece no site — s\u00f3 controle interno'}
              </div>
            </div>
            <div style={{ width: 40, height: 22, borderRadius: 11, background: form.listar_no_site ? '#8b5cf6' : '#333',
              position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 2,
                left: form.listar_no_site ? 20 : 2, transition: 'left 0.2s' }} />
            </div>
          </div>

          <div style={S.sectionTitle}>Imagem do Produto</div>
          <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp" onChange={handleImageUpload}
            style={{ marginBottom: 12, fontSize: '0.8rem' }} />

          <div style={S.canvasWrap}
            onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
            <canvas ref={canvasRef} style={S.canvas} />
            {!prodLoaded && <div style={S.canvasPlaceholder}>
              {bgLoaded ? 'Fa\u00e7a upload de um PNG acima' : 'Carregando fundo...'}
            </div>}
          </div>

          {prodLoaded && (
            <div style={{ marginTop: 12 }}>
              <label style={S.sliderLabel}>
                Tamanho: {imgScale}%
                <input type="range" min="20" max="100" value={imgScale} onChange={e => setImgScale(parseInt(e.target.value))}
                  style={S.slider} />
              </label>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-lo)', marginTop: 4 }}>
                Arraste a imagem pra posicionar. Use o slider pra redimensionar.
              </p>
              {composedUrl && <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#25d366' }}>{'\u2713'} Imagem salva</div>}
            </div>
          )}
        </div>

        {/* RIGHT — Form */}
        <div style={S.rightCol}>
          <div style={S.sectionTitle}>Informa\u00e7\u00f5es B\u00e1sicas</div>
          <div style={S.field}>
            <label style={S.label}>Nome do Produto *</label>
            <input style={S.input} value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Ex: Booster Box - Fogo Fantasmag\u00f3rico" />
          </div>
          <div style={S.field}>
            <label style={S.label}>Descri\u00e7\u00e3o</label>
            <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical' }} value={form.description}
              onChange={e => set('description', e.target.value)} placeholder="Descri\u00e7\u00e3o do produto..." />
          </div>
          <div style={S.row}>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Idioma</label>
              <select style={S.input} value={form.language} onChange={e => set('language', e.target.value)}>
                <option value="">{'\u2014'}</option>
                <option value="pt-br">Portugu\u00eas (BR)</option>
                <option value="en">Ingl\u00eas</option>
                <option value="ja">Japon\u00eas</option>
                <option value="zh">Chin\u00eas</option>
              </select>
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Tipo</label>
              <select style={S.input} value={form.tipo_item} onChange={e => set('tipo_item', e.target.value)}>
                <option value="lacrado">Lacrado</option>
                <option value="avulsa">Avulsa</option>
                <option value="acessorio">Acess\u00f3rio</option>
              </select>
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Ordem</label>
              <input type="number" style={S.input} value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
            </div>
          </div>
          <div style={S.row}>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Fornecedor</label>
              <input style={S.input} value={form.fornecedor} onChange={e => set('fornecedor', e.target.value)}
                placeholder="Nome do fornecedor" />
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Cole\u00e7\u00e3o / Set</label>
              <input style={S.input} value={form.set_colecao} onChange={e => set('set_colecao', e.target.value)}
                placeholder="Ex: Fogo Fantasmag\u00f3rico" />
            </div>
          </div>

          {/* Categoria */}
          <div style={{ ...S.sectionTitle, marginTop: 24 }}>Categoria</div>
          <div style={S.pillRow}>
            {categories.map(c => (
              <button key={c.id} onClick={() => { set('category_id', c.id); set('subcategory_ids', []); }}
                style={parseInt(form.category_id) === c.id ? S.pillActive : S.pill}>
                {c.icon} {c.name}
              </button>
            ))}
          </div>

          {/* Subcategorias */}
          {subcats.length > 0 && (
            <>
              <div style={{ ...S.sectionTitle, marginTop: 16 }}>Tags / Subcategorias</div>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-lo)', marginBottom: 8 }}>Marque todas que se aplicam:</p>
              <div style={S.pillRow}>
                {subcats.map(sc => (
                  <button key={sc.id} onClick={() => toggleSub(sc.id)}
                    style={form.subcategory_ids.includes(sc.id) ? S.pillActive : S.pill}>
                    {form.subcategory_ids.includes(sc.id) ? '\u2713 ' : ''}{sc.name}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Badge */}
          <div style={{ ...S.sectionTitle, marginTop: 24 }}>Badge</div>
          <div style={S.pillRow}>
            {BADGE_OPTIONS.map(b => (
              <button key={b.value} onClick={() => set('badge', b.value)}
                style={form.badge === b.value ? S.pillActive : S.pill}>
                {b.emoji} {b.label}
              </button>
            ))}
          </div>

          {/* Pre\u00e7o e estoque */}
          <div style={{ ...S.sectionTitle, marginTop: 24 }}>Pre\u00e7o e Estoque</div>
          <div style={S.row}>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Pre\u00e7o Venda (R$) *</label>
              <input type="number" step="0.01" min="0" style={S.input} value={form.price}
                onChange={e => set('price', e.target.value)} placeholder="0.00" />
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Pre\u00e7o Anterior</label>
              <input type="number" step="0.01" min="0" style={S.input} value={form.compare_price}
                onChange={e => set('compare_price', e.target.value)} placeholder="Riscado" />
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Custo Unit\u00e1rio (R$)</label>
              <input type="number" step="0.01" min="0" style={S.input} value={form.custo_unit}
                onChange={e => set('custo_unit', e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div style={S.row}>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Estoque</label>
              <input type="number" min="0" style={S.input} value={form.stock}
                onChange={e => set('stock', e.target.value)} />
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Estoque M\u00ednimo</label>
              <input type="number" min="0" style={S.input} value={form.estoque_min}
                onChange={e => set('estoque_min', e.target.value)} />
            </div>
            <div style={{ ...S.field, flex: 1 }}>
              <label style={S.label}>Label do pre\u00e7o</label>
              <input style={S.input} value={form.price_label} onChange={e => set('price_label', e.target.value)}
                placeholder="\u00e0 vista" />
            </div>
          </div>
          {/* Margem calculada */}
          {custoNum > 0 && precoNum > 0 && (
            <div style={{ padding: '8px 14px', borderRadius: 8, marginBottom: 12,
              background: margem >= 30 ? 'rgba(37,211,102,0.08)' : 'rgba(239,68,68,0.08)',
              border: '1px solid ' + (margem >= 30 ? 'rgba(37,211,102,0.3)' : 'rgba(239,68,68,0.3)'),
              fontSize: '0.82rem', color: margem >= 30 ? '#6ee7b7' : '#fca5a5' }}>
              Margem: {margem.toFixed(1)}% {margem < 30 ? '(abaixo de 30%)' : ''}
            </div>
          )}

          {/* Switches */}
          <div style={{ ...S.row, marginTop: 12, gap: 24 }}>
            <label style={S.checkLabel}>
              <input type="checkbox" checked={form.featured} onChange={e => set('featured', e.target.checked)} />
              Destaque na home
            </label>
            <label style={S.checkLabel}>
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
              Ativo
            </label>
          </div>

          {/* Varia\u00e7\u00f5es */}
          <div style={{ ...S.sectionTitle, marginTop: 24 }}>Varia\u00e7\u00f5es (opcional)</div>
          {variants.map((v, i) => (
            <div key={i} style={S.variantRow}>
              <input type="color" value={v.color || '#8b5cf6'} onChange={e => updateVariant(i, 'color', e.target.value)}
                style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
              <input style={{ ...S.input, width: 120 }} value={v.name} onChange={e => updateVariant(i, 'name', e.target.value)}
                placeholder="Nome da cor" />
              <input style={{ ...S.input, flex: 1 }} value={v.image_url || ''} onChange={e => updateVariant(i, 'image_url', e.target.value)}
                placeholder="URL da imagem (ou deixe vazio)" />
              <input type="number" step="0.01" style={{ ...S.input, width: 80 }} value={v.price_override || ''}
                onChange={e => updateVariant(i, 'price_override', e.target.value)} placeholder="Pre\u00e7o" />
              <input type="number" min="0" style={{ ...S.input, width: 60 }} value={v.stock || 0}
                onChange={e => updateVariant(i, 'stock', e.target.value)} placeholder="Qtd" />
              <button onClick={() => removeVariant(i)} style={{ ...S.pill, color: '#ef4444', padding: '4px 10px' }}>{'\u2715'}</button>
            </div>
          ))}
          <button onClick={addVariant} style={{ ...S.pill, marginTop: 8 }}>+ Adicionar Varia\u00e7\u00e3o</button>

          {/* Save */}
          <div style={{ marginTop: 32, display: 'flex', gap: 12 }}>
            <button onClick={handleSave} disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Salvando...' : editId ? 'Atualizar Produto' : 'Criar Produto'}
            </button>
            <button onClick={onBack} style={S.cancelBtn}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '1.5rem', maxWidth: 1100 },
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  title: { fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-hi)' },
  backBtn: { padding: '6px 14px', background: 'transparent', color: 'var(--text-mid)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer' },
  layout: { display: 'grid', gridTemplateColumns: '400px 1fr', gap: 32, alignItems: 'start' },
  leftCol: { position: 'sticky', top: 20 },
  rightCol: {},
  sectionTitle: { fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-mid)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-mid)', marginBottom: 4 },
  input: { width: '100%', padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-hi)', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  row: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  pill: { padding: '6px 14px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-mid)', transition: 'all 0.15s' },
  pillActive: { padding: '6px 14px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', background: 'var(--accent, #8b5cf6)', border: '1px solid var(--accent, #8b5cf6)', color: '#fff', transition: 'all 0.15s' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-mid)', cursor: 'pointer' },
  variantRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' },
  saveBtn: { padding: '10px 28px', background: 'var(--accent, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' },
  cancelBtn: { padding: '10px 28px', background: 'transparent', color: 'var(--text-mid)', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.85rem', cursor: 'pointer' },
  canvasWrap: { position: 'relative', width: '100%', aspectRatio: '1/1', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'move', background: '#0a0515' },
  canvas: { width: '100%', height: '100%', display: 'block' },
  canvasPlaceholder: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-lo)', fontSize: '0.85rem', pointerEvents: 'none' },
  slider: { width: '100%', marginTop: 4, accentColor: 'var(--accent, #8b5cf6)' },
  sliderLabel: { fontSize: '0.78rem', color: 'var(--text-mid)', fontWeight: 600 },
  errorMsg: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: '0.82rem', marginBottom: 16 },
  successMsg: { background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', color: '#6ee7b7', padding: '10px 14px', borderRadius: 8, fontSize: '0.82rem', marginBottom: 16 },
};
